import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resetPassword } from '@/api/auth/authApi';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/passwordInput';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';

const MINIMUM_PASSWORD_LENGTH = 10;

export function ResetPasswordScreen() {
  const { t } = useTranslation(['auth', 'admin']);
  const [searchParameters] = useSearchParams();
  const tokenParameter = searchParameters.get('token');
  const resolvedToken = tokenParameter === null ? '' : tokenParameter;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wasReset, setWasReset] = useState(false);

  async function submitReset(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      setErrorMessage(t('resetPasswordScreen.passwordHint', { count: MINIMUM_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage(t('resetPasswordScreen.mismatch'));
      return;
    }
    setIsSubmitting(true);
    try {
      await resetPassword(resolvedToken, password);
      setWasReset(true);
    } catch (resetError) {
      const backendMessage = resolveApiErrorMessage(resetError);
      setErrorMessage(backendMessage.length > 0 ? backendMessage : t('admin:errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="flex justify-center mb-7">
            <BrandLogo className="h-10 w-auto object-contain" />
          </div>

          {resolvedToken.length === 0 ? (
            <InvalidResetMessage />
          ) : wasReset === true ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-zoosuccessbg flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" className="text-xl text-zoosuccesstext" />
              </div>
              <h1 className="text-xl font-bold text-zooindigo">
                {t('resetPasswordScreen.success')}
              </h1>
              <Button asChild size="large" className="mt-6 w-full">
                <Link to={APPLICATION_ROUTES.login}>{t('resetPasswordScreen.backToLogin')}</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-7">
                <h1 className="text-2xl font-bold text-zooindigo">
                  {t('resetPasswordScreen.title')}
                </h1>
                <p className="text-sm text-zoomuted mt-2">{t('resetPasswordScreen.subtitle')}</p>
              </div>
              <form onSubmit={submitReset} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="reset-password">{t('resetPasswordScreen.passwordLabel')}</Label>
                  <PasswordInput
                    id="reset-password"
                    required
                    value={password}
                    placeholder={t('passwordPlaceholder')}
                    onChange={function updatePassword(changeEvent) {
                      setPassword(changeEvent.target.value);
                    }}
                  />
                  <p className="text-[11px] text-zoomuted">
                    {t('resetPasswordScreen.passwordHint', { count: MINIMUM_PASSWORD_LENGTH })}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reset-confirm">{t('resetPasswordScreen.confirmLabel')}</Label>
                  <PasswordInput
                    id="reset-confirm"
                    required
                    value={confirmPassword}
                    placeholder={t('passwordPlaceholder')}
                    onChange={function updateConfirm(changeEvent) {
                      setConfirmPassword(changeEvent.target.value);
                    }}
                  />
                </div>

                {errorMessage.length > 0 ? (
                  <div
                    role="alert"
                    className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
                  >
                    <Icon name="AlertCircle" />
                    {errorMessage}
                  </div>
                ) : null}

                <Button type="submit" size="large" isLoading={isSubmitting} className="w-full">
                  {t('resetPasswordScreen.submit')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InvalidResetMessage() {
  const { t } = useTranslation('auth');
  return (
    <div className="text-center py-4">
      <div className="w-14 h-14 rounded-2xl bg-zooerrorbg flex items-center justify-center mx-auto mb-4">
        <Icon name="AlertCircle" className="text-xl text-zooerrortext" />
      </div>
      <h1 className="text-xl font-bold text-zooindigo">
        {t('resetPasswordScreen.invalidTitle')}
      </h1>
      <p className="text-sm text-zoomuted mt-2">{t('resetPasswordScreen.invalidDescription')}</p>
      <Button asChild size="large" className="mt-6 w-full">
        <Link to={APPLICATION_ROUTES.forgotPassword}>{t('resetPasswordScreen.requestNew')}</Link>
      </Button>
    </div>
  );
}
